/**
 * GeneratedImageActions
 *
 * Quick Actions panel for generated images
 * Shows after successful image generation in Studio mode
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useNavigate } from 'react-router-dom';
import {
  Download,
  Dices,
  Sparkles,
  Trash2,
  BoxSelect,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui';
import { useImageActions } from '@/hooks/useImageActions';
import type { Image } from '@/types';

interface GeneratedImageActionsProps {
  image: Image;
  onNewGeneration?: () => void;
  onAddToProduct?: () => void;
}

export function GeneratedImageActions({ image, onNewGeneration, onAddToProduct }: GeneratedImageActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    deleteImage,
    downloadImage,
    generateVariation,
    isDeleting,
    isDownloading,
    isGeneratingVariation,
  } = useImageActions();

  const handleDownload = () => {
    downloadImage(image.id);
  };

  // Alternative erstellen: Neue Generation mit gleichen Settings
  const handleAlternative = () => {
    generateVariation(image.id);
    onNewGeneration?.();
  };

  // Weiterverarbeiten: Bild + Settings in Editor laden
  const handleContinueEditing = () => {
    const params = new URLSearchParams({
      sourceImageId: image.id,
      ratio: image.ratio || '1:1',
      prompt: image.prompt || '',
    });
    if (image.template_id) {
      params.set('templateId', image.template_id);
    }

    navigate(`/v2/tools/studio/single-shot?${params.toString()}`);
  };

  const handleTransferToProduct = () => {
    onAddToProduct?.();
  };

  const handleDelete = () => {
    if (window.confirm(t('gallery.deleteImageConfirm'))) {
      deleteImage(image.id);
      // Optionally: Reset view after deletion
    }
  };

  return (
    <div className="space-y-2">
      <Button className="w-full" onClick={handleDownload} disabled={isDownloading}>
        {isDownloading ? (
          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        {t('common.download')}
      </Button>

      <Button
        variant="outline"
        className="w-full"
        onClick={handleAlternative}
        disabled={isGeneratingVariation}
      >
        {isGeneratingVariation ? (
          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
        ) : (
          <Dices className="h-4 w-4 mr-2" />
        )}
        Alternative erstellen
      </Button>

      <Button variant="outline" className="w-full" onClick={handleContinueEditing}>
        <Sparkles className="h-4 w-4 mr-2" />
        Weiterverarbeiten
      </Button>

      <Button variant="outline" className="w-full" onClick={handleTransferToProduct}>
        <BoxSelect className="h-4 w-4 mr-2" />
        {t('generation.addToProduct')}
      </Button>

      <Button
        variant="outline"
        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
        onClick={handleDelete}
        disabled={isDeleting}
      >
        {isDeleting ? (
          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
        ) : (
          <Trash2 className="h-4 w-4 mr-2" />
        )}
        {t('common.delete')}
      </Button>
    </div>
  );
}

export default GeneratedImageActions;
